<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { dbRegistros } from '@/firebase/config'
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore'
import { convertFirestoreTimestamp } from '@/components/utils/functions'
import store from '@/store'
import { IPUIHistory } from '@shared/types/base'
import { IInstitutionBasePUI } from '@shared/types/pui/base'
import PUI from './PUI.vue'
import Conversor from './Conversor.vue'
interface IPUIHistoryWithId extends IPUIHistory {
	id: string
}
const currentInstitution = computed(() => store.getters['global/currentInstitution'] as string | undefined)
const historialDocumentos = ref<IPUIHistoryWithId[]>([])

const baseDataInstitution = ref<IInstitutionBasePUI | null>(null)
const isPuiReady = computed(() => Boolean(currentInstitution.value && baseDataInstitution.value))
const tabs = ref([
	{
		label: 'Validador',
		icon: 'fact_check',
		content: 'Validador'
	},
	{
		label: 'Conversor de archivo de errores',
		icon: 'error',
		content: 'Conversor'
	}
])
const activeTab = ref(tabs.value[0].content)
const getBaseDataInstitution = () => {
	const selectedInstitution = store.state.global.selectedInstitution
	if (!selectedInstitution?.Carrera?.length) {
		baseDataInstitution.value = null
		return
	}
	const currentBaseDataInstitution: IInstitutionBasePUI = {
		claveInstitucion: selectedInstitution.cveInstitution,
		carrera: selectedInstitution.Carrera.map((carrera: any) => ({
			claveCarrera: carrera.claveCarrera,
			numeroAcuerdoRvoe: Object.values(carrera.Revoes).map((rvoe: any) => ({
				numeroAcuerdoRvoe: rvoe.numero,
				fechaAcuerdoRvoe: rvoe.fechaExpedicion
			}))
		}))
	}
	baseDataInstitution.value = currentBaseDataInstitution
}
const obtenerHistorialDocumentos = async (idInstitution: string | undefined): Promise<void> => {
	if (!idInstitution) return

	const puiHistoryRef = collection(dbRegistros, 'PUI')
	const q = query(puiHistoryRef, orderBy('date', 'asc'), where('idInstitution', '==', idInstitution))

	const querySnapshot = await getDocs(q)

	if (querySnapshot.empty) {
		return
	}

	querySnapshot.forEach(doc => {
		const data = doc.data() as IPUIHistoryWithId
		historialDocumentos.value.push({
			...data,
			date: convertFirestoreTimestamp(data.date),
			id: doc.id
		})
	})
}
onMounted(async () => {
	getBaseDataInstitution()
	await obtenerHistorialDocumentos(currentInstitution.value)
})
watch(currentInstitution, async newInstitutionId => {
	historialDocumentos.value = []
	getBaseDataInstitution()
	await obtenerHistorialDocumentos(newInstitutionId)
})
</script>

<template>
	<div class="validador-page">
		<VaCard class="validador-card" outlined>
			<VaCardTitle class="validador-hero">
				<div class="validador-hero__content">
					<div class="validador-hero__brand">
						<div class="validador-hero__icon">
							<VaIcon name="fact_check" size="large" />
						</div>
						<div>
							<h1 class="validador-hero__title">Validador para la Plataforma Única de Identidad (PUI)</h1>
							<p class="validador-hero__subtitle">Valida archivos Excel conforme a los lineamientos SEP para la Plataforma Única de Identidad</p>
						</div>
					</div>
					<div class="validador-hero__chips">
						<span class="validador-chip">SEP</span>
						<span class="validador-chip validador-chip--accent">PUI</span>
						<span class="validador-chip">Excel · TXT</span>
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="validador-body">
				<VaTabs v-model="activeTab" grow>
					<template #tabs>
						<VaTab v-for="tab in tabs" :key="tab.content" :name="tab.content">
							<VaIcon :name="tab.icon" size="small" class="mr-2" />
							{{ tab.label }}
						</VaTab>
					</template>
					<VaCard class="full-width-card" style="margin-top: 1rem">
						<VaCardContent v-if="activeTab === 'Validador'">
							<PUI v-if="isPuiReady" :institution-id="currentInstitution!" :base-data-institution="baseDataInstitution!" :historial-documentos="historialDocumentos" />
						</VaCardContent>
						<VaCardContent v-if="activeTab === 'Conversor'">
							<Conversor />
						</VaCardContent>
					</VaCard>
				</VaTabs>
			</VaCardContent>
		</VaCard>
	</div>
</template>

<style scoped>
.validador-page {
	position: relative;
	width: 100%;
}
.validador-card {
	border-radius: 16px;
	overflow: hidden;
	border: 1px solid rgba(6, 35, 66, 0.1);
	box-shadow: 0 12px 32px rgba(6, 35, 66, 0.08);
}
.validador-hero {
	position: relative;
	background: #062342;
	color: #fff;
	padding: 0;
	overflow: hidden;
}
.validador-hero::after {
	content: '';
	position: absolute;
	inset: 0;
	background: rgba(36, 200, 255, 0.18);
	pointer-events: none;
}
.validador-hero__content {
	position: relative;
	z-index: 1;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1.25rem;
	padding: 1.35rem 1.5rem;
	flex-wrap: wrap;
}
.validador-hero__brand {
	display: flex;
	align-items: center;
	gap: 1rem;
}
.validador-hero__icon {
	width: 52px;
	height: 52px;
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.14);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.validador-hero__title {
	margin: 0;
	font-size: clamp(1.25rem, 2.5vw, 1.6rem);
	font-weight: 800;
	letter-spacing: -0.02em;
}
.validador-hero__subtitle {
	margin: 0.35rem 0 0;
	max-width: 560px;
	font-size: 0.9rem;
	line-height: 1.5;
	opacity: 0.88;
}
.validador-hero__chips {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	align-self: center;
}
.validador-chip {
	display: inline-flex;
	align-items: center;
	padding: 0.35rem 0.75rem;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.18);
}
.validador-chip--accent {
	background: rgba(36, 200, 255, 0.15);
	border-color: rgba(36, 200, 255, 0.35);
	color: #b8ecff;
}
.validador-body {
	padding: 1.5rem;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}
@media (max-width: 640px) {
	.validador-hero__content {
		flex-direction: column;
	}
	.validador-body {
		padding: 1rem;
	}
}
</style>
